/**
 * Money formatting for the finance module.
 *
 * The finance UI historically hardcoded `${n.toLocaleString()} EGP` in dozens
 * of places with inconsistent decimal handling. These helpers centralise that
 * so currency and precision are decided in one spot and can later be driven by
 * clinic/account settings instead of a hardcoded "EGP".
 */

/**
 * The fallback currency before the clinic has loaded. Never the answer once it
 * has: amounts are in the scope currency (see `useFormatters`), or the
 * record's own `currencyCode`.
 */
export const DEFAULT_CURRENCY = 'EGP';

function toNumber(value: number | string | null | undefined): number {
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return n === undefined || n === null || Number.isNaN(n) ? 0 : n;
}

/**
 * Format an amount as `"1,234.00 EGP"` — the standard money string used across
 * finance tables, cards and detail pages.
 *
 *   formatMoney(1234.5)            → "1,234.50 EGP"
 *   formatMoney(1234.5, 'USD')     → "1,234.50 USD"
 *   formatMoney(1234, DEFAULT, 0)  → "1,234 EGP"
 */
export function formatMoney(
  value: number | string | null | undefined,
  currency?: string,
  fractionDigits?: number,
): string {
  // One rule for every money string: Intl, the active language, and the
  // scope currency unless the caller names another. This helper used to print
  // "1,234.50 EGP" in English digits whatever the language or the currency.
  return formatMoneyIntl(toNumber(value), {
    currency: currency ?? getDisplayContext().currency,
    ...(fractionDigits !== undefined
      ? {
          minimumFractionDigits: fractionDigits,
          maximumFractionDigits: fractionDigits,
        }
      : {}),
  });
}

/**
 * Same as {@link formatMoney} but prefixes a sign: `"+1,234.00 EGP"` /
 * `"−1,234.00 EGP"`. Zero is rendered without a sign. Uses a real minus glyph
 * (−) to match the rest of the finance UI.
 */
export function formatSignedMoney(
  value: number | string | null | undefined,
  currency?: string,
  fractionDigits?: number,
): string {
  const amount = toNumber(value);
  const body = formatMoney(Math.abs(amount), currency, fractionDigits);
  if (amount > 0) return `+${body}`;
  if (amount < 0) return `−${body}`;
  return body;
}

import { formatMoneyIntl, getDisplayContext } from './format-locale';
